import type { AvailableItems, PickerItem, PickerPackage, PortalProduct, PortalProductPackage } from "@wms/domain";
import { mulQty, packageLabel, packageTotalText, packagesProblem, plainQty, qtyText, quantityProblem } from "../portal/requestForm";

/**
 * ТЗ «Рабочие столы» 1.1, UXW-39…46 (31.10.2026): the pure part of «Добавить товары» of a shipment order — the chosen
 * products and their quantities across pages and searches of one context (the order, its warehouse and its owner),
 * the quantity rules of the unit and the package (the same Decimal-safe rules as the client's request form,
 * CW31-B01/B21) and the words of the outcome of every line.
 */

/** The item in the shape of a catalog product of the client's request form: the same quantity rules apply to it. */
export function asProduct(item: PickerItem): PortalProduct {
  const p = item.storage_object;
  return {
    id: p.id,
    code: p.code,
    name: p.name,
    article: p.article,
    gtin: p.gtin,
    unit: item.unit.label,
    unit_decimals: item.unit.precision,
    shared: item.common,
    packages: item.packages.map(asPackage),
  };
}

export function asPackage(p: PickerPackage): PortalProductPackage {
  return { id: p.id, code: p.code, name: p.name, ratio: p.ratio, is_base: !!p.is_base };
}

/** A chosen product: what was typed is the number of packages when a package is chosen, base units otherwise (UXW-43). */
export interface PickEntry {
  /** the row as it was read when the product was chosen — the «было» of «Остаток изменился» */
  item: PickerItem;
  packageId: string | null;
  amount: string;
}

export interface Selection {
  /** the order, its warehouse and its owner the choice was made in */
  context: string;
  entries: Record<string, PickEntry>;
}

export function contextKey(data: Pick<AvailableItems, "order" | "warehouse" | "owner">): string {
  return `${data.order.id}|${data.warehouse.id}|${data.owner.id}`;
}

export function emptySelection(context = ""): Selection {
  return { context, entries: {} };
}

/**
 * UXW-42: the choice survives the pages and the searches of the same context; when the saved owner or warehouse of
 * the order changed, it is dropped — a choice of one client is never carried into the stock of another. The first
 * answer only names the context of an empty choice.
 */
export function inContext(selection: Selection, context: string): { selection: Selection; dropped: number } {
  if (selection.context === context) return { selection, dropped: 0 };
  if (!selection.context) return { selection: { ...selection, context }, dropped: 0 };
  return { selection: emptySelection(context), dropped: Object.keys(selection.entries).length };
}

export function choose(selection: Selection, item: PickerItem): Selection {
  const id = item.storage_object.id;
  if (selection.entries[id]) return selection;
  return { ...selection, entries: { ...selection.entries, [id]: { item, packageId: null, amount: "" } } };
}

export function unchoose(selection: Selection, id: string): Selection {
  if (!selection.entries[id]) return selection;
  const entries = { ...selection.entries };
  delete entries[id];
  return { ...selection, entries };
}

/** A typed amount or another package; choosing a package keeps the typed number, its meaning is shown at once. */
export function updateEntry(selection: Selection, item: PickerItem, patch: Partial<Pick<PickEntry, "packageId" | "amount">>): Selection {
  const id = item.storage_object.id;
  const current = selection.entries[id] ?? { item, packageId: null, amount: "" };
  return { ...selection, entries: { ...selection.entries, [id]: { ...current, ...patch } } };
}

export function chosen(selection: Selection): PickEntry[] {
  return Object.values(selection.entries);
}

export function packageOf(entry: PickEntry): PickerPackage | undefined {
  return entry.packageId ? entry.item.packages.find((p) => p.id === entry.packageId) : undefined;
}

/** The quantity of the line in base units: «2» boxes of 50 → "100"; null until it is a number. */
export function baseQuantity(entry: PickEntry): string | null {
  const pkg = packageOf(entry);
  if (pkg) return mulQty(entry.amount, pkg.ratio);
  return plainQty(entry.amount);
}

/** UXW-43: «2 × Короб (50 шт) = 100 шт» — the number of packages is never mixed with base units. */
export function conversionText(entry: PickEntry): string | null {
  const pkg = packageOf(entry);
  return pkg ? packageTotalText(entry.amount, asPackage(pkg), entry.item.unit.label) : null;
}

/** The option of a package in the select: «Короб — 50 шт». */
export function packageOption(pkg: PickerPackage, unit: string): string {
  return packageLabel(asPackage(pkg), unit);
}

/** What is wrong with the typed amount (empty, not a number, ≤ 0, a fraction of pieces), or null. */
export function entryProblem(entry: PickEntry): string | null {
  const pkg = packageOf(entry);
  const product = asProduct(entry.item);
  return pkg ? packagesProblem(entry.amount, asPackage(pkg), product) : quantityProblem(entry.amount, product);
}

/** Compares two Decimal strings without a float: -1, 0, 1; null when either is not a number. */
export function compareQty(a: unknown, b: unknown): number | null {
  const x = plainQty(a);
  const y = plainQty(b);
  if (x === null || y === null) return null;
  const [xw, xf = ""] = x.replace("-", "").split(".");
  const [yw, yf = ""] = y.replace("-", "").split(".");
  const scale = Math.max(xf.length, yf.length);
  const xi = BigInt(xw + xf.padEnd(scale, "0")) * (x.startsWith("-") ? -1n : 1n);
  const yi = BigInt(yw + yf.padEnd(scale, "0")) * (y.startsWith("-") ? -1n : 1n);
  return xi === yi ? 0 : xi < yi ? -1 : 1;
}

/**
 * UXW-46: more than is available now is a warning, not a new rule — a deficit order may still be planned as before;
 * the server checks the stock when the order is planned and posted.
 */
export function availabilityWarning(entry: PickEntry, available: string = entry.item.available): string | null {
  const base = baseQuantity(entry);
  if (base === null || compareQty(base, available) !== 1) return null;
  const unit = entry.item.unit.label;
  return `Больше, чем доступно сейчас: доступно ${qtyText(available)} ${unit}, указано ${qtyText(base)} ${unit}. Строку можно добавить — остаток проверит сервер при планировании.`;
}

/** UXW-44: the chosen row read again before adding — «Остаток изменился: было …, сейчас …» or null. */
export function stockChange(before: PickerItem, after: PickerItem | undefined): string | null {
  const unit = before.unit.label;
  if (!after) return "Товара больше нет в остатках владельца на складе заказа; обновите выбор";
  if (compareQty(before.available, after.available) === 0) return null;
  return `Остаток изменился: было ${qtyText(before.available)} ${unit}, сейчас ${qtyText(after.available)} ${unit}; уменьшите количество или обновите выбор`;
}

/** The body of the ordinary «Строка заказа» create: a package line sends the package and its number (the server
 * computes the base quantity, WMS-04), any other line its base quantity. */
export function linePayload(orderId: string, entry: PickEntry): Record<string, string | null> {
  const pkg = packageOf(entry);
  const base = { document_id: orderId, storage_object_id: entry.item.storage_object.id };
  return pkg ? { ...base, package_id: pkg.id, packages_count: plainQty(entry.amount) } : { ...base, quantity_plan: plainQty(entry.amount) };
}

/** The line in the preview: «CUP — Стаканы: 2 × Короб (50 шт) = 100 шт» or «… : 30 шт». */
export function previewText(entry: PickEntry): string {
  const p = entry.item.storage_object;
  const what = conversionText(entry) ?? `${qtyText(baseQuantity(entry) ?? entry.amount)} ${entry.item.unit.label}`;
  return `${p.code} — ${p.name}: ${what}`;
}

const FIELD_WORDS: Record<string, string> = {
  quantity_plan: "Количество",
  packages_count: "Упаковок",
  package_id: "Упаковка",
  storage_object_id: "Товар",
  document_id: "Заказ",
  batch_id: "Партия",
  state_id: "Состояние",
};

/** UXW-57: «Товары → <товар> → Количество: …» — where, what is wrong, how to fix (the server text says the last two). */
export function lineError(entry: PickEntry, field: string | null | undefined, message: string): string {
  const where = `Товары → ${entry.item.storage_object.code} ${entry.item.storage_object.name}`;
  return `${where} → ${(field && FIELD_WORDS[field]) || "Строка"}: ${message}`;
}

export type LineOutcome = { ok: true; message: string } | { ok: false; message: string; unknown?: boolean };

/** UXW-45: the products still to add — never run or refused; an added line is never sent again. */
export function pendingIds(selection: Selection, outcomes: Record<string, LineOutcome>): string[] {
  return Object.keys(selection.entries).filter((id) => !outcomes[id]?.ok);
}

/** a + b of two non-negative Decimal strings, exactly: "0.1" + "0.2" = "0.3". */
export function addQty(a: string, b: string): string | null {
  const x = plainQty(a);
  const y = plainQty(b);
  if (x === null || y === null || x.startsWith("-") || y.startsWith("-")) return null;
  const [xw, xf = ""] = x.split(".");
  const [yw, yf = ""] = y.split(".");
  const scale = Math.max(xf.length, yf.length);
  const total = BigInt(xw + xf.padEnd(scale, "0")) + BigInt(yw + yf.padEnd(scale, "0"));
  const digits = total.toString().padStart(scale + 1, "0");
  return plainQty(scale ? `${digits.slice(0, -scale)}.${digits.slice(-scale)}` : digits);
}

/**
 * UXW-59: an answer lost after the request (network) is not a refusal: before a retry the line is looked for — when the
 * plan of this product in the order grew by this line since the moment before the attempt, the line is already there.
 */
export function alreadyAdded(inOrderBefore: string, entry: PickEntry, after: PickerItem | undefined): boolean {
  const base = baseQuantity(entry);
  const expected = base === null ? null : addQty(inOrderBefore, base);
  return !!after && expected !== null && compareQty(after.in_order, expected) !== -1;
}
